/**
 * Tabs — headless primitive wrapping Radix Tabs.
 *
 * Radix's Tabs owns the WAI-ARIA Tabs pattern: `role="tablist"` /
 * `role="tab"` / `role="tabpanel"` wiring, arrow-key + Home/End
 * navigation, automatic vs. manual activation modes, and
 * `aria-controls` / `aria-labelledby` pairing (ARCHITECTURE §6). We
 * wrap thinly:
 *   - Permission gating on <Root> (denied → null + permission:denied)
 *   - Audit emission on tab change (`tabs:change`)
 *   - `data-oshon-primitive="tabs-*"` styling hooks
 *
 * "use client" mandatory — Radix uses hooks + DOM APIs internally.
 */

'use client';

import * as RadixTabs from '@radix-ui/react-tabs';
import { forwardRef } from 'react';
import type {
  ComponentPropsWithoutRef,
  ElementRef,
  ReactNode,
} from 'react';
import {
  evaluatePermission,
  resolvePermissions,
  usePermissions,
  useAudit,
  type PermissionContext,
} from '../context.js';
import { CANONICAL_ACTIONS } from '../actions.js';

// ────────────────────────────────────────────────────────────────────────────
// Root

export interface TabsRootProps
  extends Omit<RadixTabs.TabsProps, 'onValueChange'> {
  /**
   * Fires whenever the active tab changes. The primitive fires
   * `tabs:change` audit automatically before this callback runs.
   */
  onValueChange?: (value: string) => void;
  /**
   * Per-instance permission override. Merges over the ambient
   * `PermissionContext`. Denied + any mode hides the entire tabs
   * surface — tabs are navigation scaffolding; no useful visible-but-
   * inert state — and emits a `permission:denied` audit.
   */
  permissions?: Partial<PermissionContext>;
  /**
   * Resource name for `permissions.can('view', resource, attrs)`.
   * Default `'tabs'`. Override for e.g. `resource="admin:settings-tabs"`.
   */
  resource?: string;
  /** Attribute bag for attribute-based access control. */
  permissionAttrs?: Record<string, unknown>;
  children: ReactNode;
}

/**
 * Tabs root. Orchestrates the tablist + panels, gates on permissions,
 * fires `tabs:change` audit. Must wrap `Tabs.List` + `Tabs.Trigger*` +
 * `Tabs.Content*`.
 */
function Root({
  children,
  value,
  defaultValue,
  onValueChange,
  permissions,
  resource = 'tabs',
  permissionAttrs,
  orientation,
  dir,
  activationMode,
  ...rest
}: TabsRootProps) {
  const context = usePermissions();
  const effective = resolvePermissions(permissions, context);
  const emit = useAudit();

  const { granted, denyReason } = evaluatePermission(
    effective,
    'view',
    resource,
    permissionAttrs,
  );

  if (!granted) {
    emit({
      component: 'tabs',
      action: CANONICAL_ACTIONS.PERMISSION_DENIED,
      permissionResult: 'denied',
      ...(denyReason ? { denyReason } : {}),
      details: { attemptedAction: 'view', resource },
    });
    return null;
  }

  const handleValueChange = (next: string) => {
    emit({
      component: 'tabs',
      action: CANONICAL_ACTIONS.TABS_CHANGE,
      permissionResult: 'granted',
      details: { value: next },
    });
    onValueChange?.(next);
  };

  return (
    <RadixTabs.Root
      value={value}
      defaultValue={defaultValue}
      onValueChange={handleValueChange}
      orientation={orientation}
      dir={dir}
      activationMode={activationMode}
      data-oshon-primitive="tabs-root"
      {...rest}
    >
      {children}
    </RadixTabs.Root>
  );
}
Root.displayName = 'Tabs.Root';

// ────────────────────────────────────────────────────────────────────────────
// List

export type TabsListProps = ComponentPropsWithoutRef<typeof RadixTabs.List>;

const List = forwardRef<ElementRef<typeof RadixTabs.List>, TabsListProps>(
  function TabsList(props, ref) {
    return (
      <RadixTabs.List
        ref={ref}
        data-oshon-primitive="tabs-list"
        {...props}
      />
    );
  },
);

// ────────────────────────────────────────────────────────────────────────────
// Trigger

export type TabsTriggerProps = ComponentPropsWithoutRef<typeof RadixTabs.Trigger>;

const Trigger = forwardRef<
  ElementRef<typeof RadixTabs.Trigger>,
  TabsTriggerProps
>(function TabsTrigger(props, ref) {
  return (
    <RadixTabs.Trigger
      ref={ref}
      data-oshon-primitive="tabs-trigger"
      {...props}
    />
  );
});

// ────────────────────────────────────────────────────────────────────────────
// Content

export type TabsContentProps = ComponentPropsWithoutRef<typeof RadixTabs.Content>;

const Content = forwardRef<
  ElementRef<typeof RadixTabs.Content>,
  TabsContentProps
>(function TabsContent(props, ref) {
  return (
    <RadixTabs.Content
      ref={ref}
      data-oshon-primitive="tabs-content"
      {...props}
    />
  );
});

// ────────────────────────────────────────────────────────────────────────────
// Compound export

/**
 * Compound Tabs primitive. Use as:
 *
 *   <Tabs.Root defaultValue="overview">
 *     <Tabs.List>
 *       <Tabs.Trigger value="overview">Overview</Tabs.Trigger>
 *       <Tabs.Trigger value="activity">Activity</Tabs.Trigger>
 *     </Tabs.List>
 *     <Tabs.Content value="overview">…</Tabs.Content>
 *     <Tabs.Content value="activity">…</Tabs.Content>
 *   </Tabs.Root>
 */
export const Tabs = {
  Root,
  List,
  Trigger,
  Content,
};

export type TabsProps = TabsRootProps;
